"use client";
import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../../../components/ui/button";
import { Order } from "../types/order";
import { OrderCard } from "./OrderCard";
import axios from "axios";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../config/api";
import { FileText } from "lucide-react";
import { ListPaginationFooter } from "../../../components/ListPaginationFooter";
import { Loader } from "../../../components/Loader";
import { formatRoleLabel } from "../../../utils/roles";

export const InvoicesPage: React.FC<{ userRole: "admin" | "dealer" | "employee"; userId: string }> = ({
  userRole,
  userId,
}) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const maxResults = 6;
  const [createdBy, setCreatedBy] = useState<string>("");
  const [customer, setCustomer] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const [users, setUsers] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const { authToken = "", id = "", role = "", username = "" } = useSelector((state: any) => state.loginData || {});
  const isAdmin = String(role || userRole).toLowerCase() === "admin";
  const isStaff = String(role || userRole).toLowerCase() === "employee";
  const isDealer = String(role || userRole).toLowerCase() === "dealer";

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/users`, {
        params: { firstResults: 0, maxResults: "all", role: "creator" },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setUsers(res.data.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/users`, {
        params: { firstResults: 0, maxResults: "all", role: "all" },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      let list: any[] = res.data.users || [];
      if (isStaff && username) {
        try {
          const ordersRes = await axios.get(`${API_BASE_URL}account/${id}/orders`, {
            params: {
              firstResults: 0,
              maxResults: 5000,
              status: "all",
              lite: true,
              createdBy: username,
            },
            headers: { Authorization: `Bearer ${authToken}` },
          });
          const ids = new Set(
            (ordersRes.data.orders || []).map((o: any) => String(o.customerId || ""))
          );
          list = list.filter((u) => ids.has(String(u.id)));
        } catch (e) {
          console.error("Error loading staff invoice customers:", e);
        }
      }
      setCustomers(list);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const ensureFilterOptions = async () => {
    if (filtersLoaded || !id || !authToken) return;
    await Promise.all([fetchUsers(), fetchCustomers()]);
    setFiltersLoaded(true);
  };

  const fetchOrders = useCallback(
    async (pageNum = 1) => {
      if (!id || !authToken) return;
      try {
        setLoading(true);
        const firstResults = (pageNum - 1) * maxResults;
        const res = await axios.get(`${API_BASE_URL}account/${id}/orders`, {
          params: {
            firstResults,
            maxResults,
            invoice: true,
            lite: true,
            createdBy,
            customer,
            fromDate,
            toDate,
          },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setOrders(res.data.orders || []);
        setTotalCount(res.data.totalCount || 0);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    },
    [id, authToken, createdBy, customer, fromDate, toDate]
  );

  useEffect(() => {
    if (!id || !authToken) return;
    fetchOrders(page);
  }, [page, id, authToken, fetchOrders]);

  useEffect(() => {
    if (id && authToken) ensureFilterOptions();
  }, [id, authToken]);

  const totalPages = Math.ceil(totalCount / maxResults) || 1;

  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages, p + 1));

  const applyFilters = () => {
    setPage(1);
    fetchOrders(1);
  };

  // Keep rest of JSX from original — read file for filter UI
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="text-orange-600" size={32} />
            Invoices
          </h1>
          <p className="text-gray-500 text-sm mt-2">View and manage all your invoices efficiently</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border-2 border-gray-200 p-4 flex flex-col gap-3 lg:flex-row lg:flex-wrap xl:flex-nowrap lg:items-end">
          {isAdmin && (
            <div className="flex flex-col w-full lg:flex-1 lg:min-w-[140px]">
              <label className="text-sm font-medium text-gray-600 mb-1">Created By</label>
              <select
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
                className="w-full border rounded-lg px-3 h-10 text-sm bg-white"
              >
                <option value="">All</option>
                {users.map((u) => (
                  <option key={u.id} value={u.username}>
                    {u.username}
                    {u.role ? ` (${formatRoleLabel(u.role)})` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!isDealer && (
            <div className="flex flex-col w-full lg:flex-1 lg:min-w-[140px]">
              <label className="text-sm font-medium text-gray-600 mb-1">Customer</label>
              <select
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                className="w-full border rounded-lg px-3 h-10 text-sm bg-white"
              >
                <option value="">All</option>
                {customers.map((u) => (
                  <option key={u.id} value={String(u.id)}>
                    {u.companyName || u.username}
                    {u.role ? ` (${formatRoleLabel(u.role)})` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex flex-col w-full sm:w-[calc(50%-0.375rem)] lg:w-[150px] lg:flex-none">
            <label className="text-sm font-medium text-gray-600 mb-1">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full border rounded-lg px-3 h-10 text-sm bg-white"
            />
          </div>
          <div className="flex flex-col w-full sm:w-[calc(50%-0.375rem)] lg:w-[150px] lg:flex-none">
            <label className="text-sm font-medium text-gray-600 mb-1">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full border rounded-lg px-3 h-10 text-sm bg-white"
            />
          </div>
          <div className="w-full lg:w-auto lg:flex-none flex flex-row gap-2">
            <Button
              onClick={applyFilters}
              className="bg-orange-600 hover:bg-orange-700 text-white h-10 px-5 flex-1 sm:flex-none whitespace-nowrap"
            >
              Apply
            </Button>
            <Button
              variant="outline"
              className="h-10 px-5 flex-1 sm:flex-none whitespace-nowrap"
              onClick={() => {
                setCreatedBy("");
                setCustomer("");
                setFromDate("");
                setToDate("");
                setPage(1);
              }}
            >
              Clear
            </Button>
          </div>
        </div>

      {loading && orders.length === 0 ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-gray-200 p-12 text-center">
          <FileText size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-600 font-medium text-lg">No invoices found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {orders.map((order) => (
            <OrderCard key={order.id} order={order} refresh={() => fetchOrders(page)} />
          ))}
        </div>
      )}

      <ListPaginationFooter
        page={page}
        totalPages={totalPages}
        totalCount={totalCount}
        currentCount={orders.length}
        itemLabel="invoices"
        accent="orange"
        onPrev={handlePrev}
        onNext={handleNext}
      />
    </div>
  );
};
